import * as numeral from "numeral";
import { useCallback } from "react";

import { useAppConfig } from "../../contexts/app/config";
import { useMarkets } from "../../contexts/market";
import { getTokenBySymbol } from "../../utils/tokenInfo";
import { calculatePriceOrBalance } from "../../utils/utils";

type OptionsArgs = {
  formatLargeNumbers?: boolean;
  maximumFractionDigits?: number;
  hideable?: boolean;
};

export type CalculatePriceForDisplayCurrency = (value: number | null, options?: OptionsArgs) => string;

export const useCalculatePriceForDisplayCurrency = (): CalculatePriceForDisplayCurrency => {
  const {
    displayCurrency,
    fiatDisplayCurrencies,
    cryptoDisplayCurrencies,
    exchangeRateForDisplayedCurrency,
    marketDataMap,
  } = useMarkets();
  const { valuesHidden, hideableValueWrapper } = useAppConfig();

  const defaultMaximumFractionDigits =
    fiatDisplayCurrencies[displayCurrency] ?? cryptoDisplayCurrencies[displayCurrency];

  const calculatePriceForDisplayCurrency = useCallback<CalculatePriceForDisplayCurrency>(
    (value, options) => {
      options = {
        ...{ formatLargeNumbers: false, maximumFractionDigits: defaultMaximumFractionDigits },
        ...options,
      };
      const { formatLargeNumbers, maximumFractionDigits } = options;

      const isDisplayCurrencyFiat = Object.prototype.hasOwnProperty.call(fiatDisplayCurrencies, displayCurrency);

      if (value !== null && value <= 0) {
        return (
          (value < 0 ? "-" : "") + calculatePriceOrBalance(displayCurrency, Math.abs(value), maximumFractionDigits)
        );
      }

      let displayCurrencyPriceInUSD: number | undefined;
      if (isDisplayCurrencyFiat) {
        if (displayCurrency === "USD") {
          displayCurrencyPriceInUSD = 1;
        } else {
          displayCurrencyPriceInUSD = 1 / exchangeRateForDisplayedCurrency;
        }
      } else {
        // display currency is crypto so we get its price
        const mintAddress = getTokenBySymbol(displayCurrency)?.address ?? "";
        displayCurrencyPriceInUSD = marketDataMap.get(mintAddress)?.price;
      }

      let correctedValue: number | null = null;
      if (value && displayCurrencyPriceInUSD) {
        correctedValue = value / displayCurrencyPriceInUSD;
      }

      const currencyString = calculatePriceOrBalance(displayCurrency, correctedValue, maximumFractionDigits);
      if (formatLargeNumbers) {
        return currencyString[0] + numeral(currencyString).format("0.0a");
      } else {
        return currencyString;
      }
    },
    [
      defaultMaximumFractionDigits,
      displayCurrency,
      exchangeRateForDisplayedCurrency,
      fiatDisplayCurrencies,
      marketDataMap,
    ],
  );

  const hideValuesCallback = useCallback(
    (value: number | null, options: OptionsArgs | undefined): string => {
      options = {
        ...{ hideable: true },
        ...options,
      };
      if (options.hideable) {
        return hideableValueWrapper(value);
      } else {
        return calculatePriceForDisplayCurrency(value, options);
      }
    },
    [calculatePriceForDisplayCurrency, hideableValueWrapper],
  );

  return valuesHidden ? hideValuesCallback : calculatePriceForDisplayCurrency;
};

export const useCalculatePriceForDisplayCurrencyNumeric = () => {
  const { displayCurrency, fiatDisplayCurrencies, exchangeRateForDisplayedCurrency, marketDataMap } = useMarkets();

  return (price: number): number => {
    if (price <= 0) {
      return 0;
    }
    const isDisplayCurrencyFiat = Object.prototype.hasOwnProperty.call(fiatDisplayCurrencies, displayCurrency);

    let displayCurrencyPriceInUSD: number | undefined;
    if (isDisplayCurrencyFiat) {
      if (displayCurrency === "USD") {
        displayCurrencyPriceInUSD = 1;
      } else {
        displayCurrencyPriceInUSD = 1 / exchangeRateForDisplayedCurrency;
      }
    } else {
      // display currency is crypto so we get its price
      const mintAddress = getTokenBySymbol(displayCurrency)?.address ?? "";
      displayCurrencyPriceInUSD = marketDataMap.get(mintAddress)?.price;
    }

    if (displayCurrencyPriceInUSD) {
      return price / displayCurrencyPriceInUSD;
    }

    return 0;
  };
};
